import { supabase } from './supabase'
import { loadFromLocalStorage, getAllAutosavedSessions, clearLocalStorage } from './autosave'
import { loadDraft } from './draftSaving'

/**
 * Get all saved drafts for a user (localStorage + server)
 */
export const getAllDrafts = async (organizationId, userId, formType = 'examination') => {
  const drafts = []

  // Get localStorage drafts (only those from last 24 hours)
  const localSessions = getAllAutosavedSessions()
  const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000
  localSessions.forEach((session) => {
    const savedAt = session.savedAt ? new Date(session.savedAt).getTime() : Date.now()
    // Filter out drafts older than 24 hours
    if (savedAt < twentyFourHoursAgo) return
    
    // For examination form type
    if (formType === 'examination' && (session.formData || session.customQuestions)) {
      drafts.push({
        id: `local_${session.id}`,
        source: 'local',
        formType: 'examination',
        savedAt: new Date(savedAt),
        formData: session.formData,
        customQuestions: session.customQuestions,
        currentStep: session.currentStep || 0,
      })
    }
    // For session form type
    else if (formType === 'session' && (session.sessionDuration !== undefined || session.vitalSigns || session.quickNotes || session.sessionData)) {
      drafts.push({
        id: `local_${session.id}`,
        source: 'local',
        formType: 'session',
        savedAt: new Date(savedAt),
        sessionDuration: session.sessionDuration,
        vitalSigns: session.vitalSigns,
        quickNotes: session.quickNotes,
        sessionData: session.sessionData,
        selectedClientId: session.selectedClientId,
        vitalSignsInterval: session.vitalSignsInterval,
      })
    }
  })

  // Get server drafts (only those from last 24 hours)
  if (organizationId && userId) {
    try {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      const { data: serverDrafts, error } = await supabase
        .from('form_drafts')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .eq('form_type', formType)
        .gte('updated_at', twentyFourHoursAgo) // Only get drafts from last 24 hours
        .order('updated_at', { ascending: false })

      if (error) {
        console.error('Error fetching server drafts:', error)
      } else if (serverDrafts) {
      serverDrafts.forEach((draft) => {
        if (draft.draft_data) {
          const draftData = {
            id: `server_${draft.id}`,
            source: 'server',
            formType: draft.form_type,
            savedAt: new Date(draft.updated_at),
            serverId: draft.id,
            formId: draft.form_id || draft.draft_data.formId, // Get formId from draft_data if form_id is null
          }
          
          // Add form-specific data
          if (draft.form_type === 'examination') {
            draftData.formData = draft.draft_data.formData
            draftData.customQuestions = draft.draft_data.customQuestions
            draftData.currentStep = draft.draft_data.currentStep || 0
          } else if (draft.form_type === 'session') {
            draftData.sessionDuration = draft.draft_data.sessionDuration
            draftData.vitalSigns = draft.draft_data.vitalSigns
            draftData.quickNotes = draft.draft_data.quickNotes
            draftData.sessionData = draft.draft_data.sessionData
            draftData.selectedClientId = draft.draft_data.selectedClientId
            draftData.vitalSignsInterval = draft.draft_data.vitalSignsInterval
          }
          
          drafts.push(draftData)
        }
      })
      }
    } catch (error) {
      console.error('Error fetching server drafts:', error)
    }
  }

  // Sort by most recent
  return drafts.sort((a, b) => b.savedAt - a.savedAt)
}

/**
 * Load a specific draft
 */
export const loadDraftById = async (draftId, organizationId, userId) => {
  if (draftId.startsWith('local_')) {
    const sessionId = draftId.replace('local_', '')
    return loadFromLocalStorage(sessionId)
  } else if (draftId.startsWith('server_')) {
    const serverId = draftId.replace('server_', '')
    const { data, error } = await supabase
      .from('form_drafts')
      .select('*')
      .eq('id', serverId)
      .single()

    if (error) throw error
    return data?.draft_data || null
  }
  return null
}

/**
 * Delete a draft
 */
export const deleteDraftById = async (draftId, organizationId, userId, formType = 'examination') => {
  if (draftId.startsWith('local_')) {
    const sessionId = draftId.replace('local_', '')
    clearLocalStorage(sessionId)
    return true
  } else if (draftId.startsWith('server_')) {
    const serverId = draftId.replace('server_', '')
    // Delete by draft ID directly
    try {
      const { error } = await supabase
        .from('form_drafts')
        .delete()
        .eq('id', serverId)
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting server draft:', error)
      throw error
    }
  }
  return false
}

/**
 * Bulk delete drafts (optimized for performance)
 */
export const bulkDeleteDrafts = async (draftIds, organizationId, userId, formType = 'examination') => {
  const localDraftIds = []
  const serverDraftIds = []

  // Separate local and server drafts
  draftIds.forEach((draftId) => {
    if (draftId.startsWith('local_')) {
      localDraftIds.push(draftId.replace('local_', ''))
    } else if (draftId.startsWith('server_')) {
      serverDraftIds.push(draftId.replace('server_', ''))
    }
  })

  const results = {
    localDeleted: 0,
    serverDeleted: 0,
    errors: [],
  }

  // Delete local drafts (fast, synchronous)
  localDraftIds.forEach((sessionId) => {
    try {
      clearLocalStorage(sessionId)
      results.localDeleted++
    } catch (error) {
      console.error('Error deleting local draft:', error)
      results.errors.push({ draftId: `local_${sessionId}`, error })
    }
  })

  // Batch delete server drafts in a single query
  if (serverDraftIds.length > 0) {
    try {
      const { error } = await supabase
        .from('form_drafts')
        .delete()
        .in('id', serverDraftIds)
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .eq('form_type', formType)

      if (error) {
        throw error
      }
      results.serverDeleted = serverDraftIds.length
    } catch (error) {
      console.error('Error bulk deleting server drafts:', error)
      // If batch delete fails, try individual deletes as fallback
      for (const serverId of serverDraftIds) {
        try {
          const { error } = await supabase
            .from('form_drafts')
            .delete()
            .eq('id', serverId)
            .eq('organization_id', organizationId)
            .eq('user_id', userId)

          if (error) throw error
          results.serverDeleted++
        } catch (individualError) {
          console.error(`Error deleting server draft ${serverId}:`, individualError)
          results.errors.push({ draftId: `server_${serverId}`, error: individualError })
        }
      }
    }
  }

  return results
}

